import axios, {InternalAxiosRequestConfig} from 'axios';
import {API_URL, DEFAULT_COORDINATES} from 'config';
import {getCookie} from 'react-use-cookie';
import {LanguageEnum, StorageKeyEnum} from 'types';
import {authUtil} from 'utils/auth';
import {getLocaleFromUrl, removeQuotesFromString} from 'utils/helpers';
import logger from 'utils/logger';

import {responseSuccessHandler} from './interceptors';
import {memoizedRefreshTokenFn} from './refresh';

const defaultConfig = {
  baseURL: API_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
};

export const api = axios.create(defaultConfig);
/**
 * Request Interceptor
 */
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (typeof window !== 'undefined') {
      try {
        const lsCoords = window?.localStorage?.getItem(StorageKeyEnum.COORDINATES);
        const regionCoords = window?.localStorage?.getItem(StorageKeyEnum.REGION_COORDS);
        const coordinates = lsCoords
          ? removeQuotesFromString(lsCoords)
          : regionCoords
          ? removeQuotesFromString(regionCoords)
          : DEFAULT_COORDINATES;

        config.headers.set('Location-Coordinates', coordinates);
      } catch (error: any) {
        logger.addBreadcrumb('api:interceptor', {error});
        logger.logDebug(`${error}`, 'api:interceptor');
      }

      // Use cookie as source of truth for locale, fallback to URL if cookie doesn't exist
      const localeFromCookie = getCookie('NEXT_LOCALE') as LanguageEnum;
      const locale = localeFromCookie || getLocaleFromUrl();
      config.headers.set('Accept-Language', locale);
    }

    const accessToken = authUtil.getAccessToken();
    if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`);

    return config;
  },
  (error) => {
    logger.error(error);
    return Promise.reject(error);
  },
);

/**
 * Response Interceptor
 */
api.interceptors.response.use(responseSuccessHandler, async (error) => {
  const config = error?.config;

  if (error?.response?.status === 401 && !config?.sent) {
    config.sent = true;

    const data = await memoizedRefreshTokenFn();
    if (data) {
      config.headers['Authorization'] = 'Bearer ' + data.access;
      return api(config);
    }
  } else {
    logger.error(error);
  }
  return Promise.reject(error);
});
